import type { AxiosInstance } from 'axios'
import type { IFactoryFunctionRequest, IFactoryResponse, ITraining } from '@bc-library/interface'
import { EMethod } from '@bc-library/interface'
import useAxios from '@/services/api/useAxios'
import { API_MAIN_VERSION, BASE_URL } from '@/config/constantes'
import { afterReceivingOne, afterReceivingOneTrainingWithSessions } from './hooks'
import { afterReceiving as afterReceivingSession} from '../trainingSession/hooks'

const resource = 'trainings'

const filter = (axios: IFactoryFunctionRequest<Array<ITraining>, Array<ITraining>>) => ({ search, limit, page, retrieveNotActive = false }: { search: string; limit: number; page: number, retrieveNotActive?: boolean }): IFactoryResponse<Array<ITraining>, Array<ITraining>> => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/filter`,
    query: {
      search,
      limit,
      page,
      retrieveNotActive
    },
  })
}

const createOne = (axios: IFactoryFunctionRequest) => (trainingTemplate: ITraining): IFactoryResponse<ITraining> => {
  return axios({
    method: EMethod.post,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/`,
    data: trainingTemplate,
  })
}

const getOne = (axios: IFactoryFunctionRequest) => (_id: string): IFactoryResponse<ITraining> => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/${_id}`,
  })
}

const updateOne = (axios: IFactoryFunctionRequest) => (_id: string, training: ITraining): IFactoryResponse<ITraining> => {
  // console.log('training', training);

  return axios({
    method: EMethod.put,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/${_id}`,
    data: training,
  })
}

const getAll = (axios: IFactoryFunctionRequest) => ({ firmId }: { firmId: string }): IFactoryResponse<Array<ITraining>> => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/firm/${firmId}`,
    afterReceivingHook: (data) => ({
      ...data,
      docs: data.docs.map((training) => afterReceivingOne(training)),
    }),
  })
}

const catalog = (axios: IFactoryFunctionRequest) => (query): IFactoryResponse<Array<ITraining>> => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/catalog`,
    query,
    afterReceivingHook: (data) => {
      return {
        ...data,
        docs: data.docs.map((training) => ({
          ...afterReceivingOne(training),
          plannedSessions: afterReceivingSession(training.plannedSessions),
        })),
      }
    },
  })
}


const catalogTraingDetail = (axios: IFactoryFunctionRequest) => (trainingId): IFactoryResponse<Array<ITraining>> => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/catalog/details/${trainingId}`,
    afterReceivingHook: afterReceivingOneTrainingWithSessions
  })
}

const addDocument = (axios: IFactoryFunctionRequest) => (_id: string, file): IFactoryResponse<ITraining> => {
  return axios({
    method: EMethod.post,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/document/${_id}`,
    data: file,
  })
}

const getDetails = (axios: IFactoryFunctionRequest) => (trainingId: string): IFactoryResponse<Array<ITraining>> => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/details/${trainingId}`,
  })
}

const getWishes = (axios: IFactoryFunctionRequest) => (trainingId: string): IFactoryResponse<any> => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/details/wishes/${trainingId}`,
  })
}

// ==================== V2 CRUD Endpoints ====================

const toggleActive = (axios: IFactoryFunctionRequest) => (trainingId: string): IFactoryResponse<{ _id: string, isActive: boolean }> => {
  return axios({
    method: EMethod.patch,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/${trainingId}/toggle-active`,
  })
}

const softDelete = (axios: IFactoryFunctionRequest) => (trainingId: string): IFactoryResponse<{ success: boolean, message: string }> => {
  return axios({
    method: EMethod.delete,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/${trainingId}`,
  })
}

const duplicate = (axios: IFactoryFunctionRequest) => (trainingId: string, display?: string): IFactoryResponse<ITraining> => {
  return axios({
    method: EMethod.post,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/${trainingId}/duplicate`,
    data: display ? { display } : {},
  })
}

// Checklist endpoints
const getChecklist = (axios: IFactoryFunctionRequest) => (trainingId: string): IFactoryResponse<any> => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/${trainingId}/checklist`,
  })
}

const addChecklistItem = (axios: IFactoryFunctionRequest) => (trainingId: string, display: string): IFactoryResponse<any> => {
  return axios({
    method: EMethod.post,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/${trainingId}/checklist`,
    data: { display },
  })
}

const updateChecklistItem = (axios: IFactoryFunctionRequest) => (trainingId: string, itemId: string, data: { display?: string, completed?: boolean }): IFactoryResponse<any> => {
  return axios({
    method: EMethod.patch,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/${trainingId}/checklist/${itemId}`,
    data,
  })
}

const deleteChecklistItem = (axios: IFactoryFunctionRequest) => (trainingId: string, itemId: string): IFactoryResponse<any> => {
  return axios({
    method: EMethod.delete,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/${trainingId}/checklist/${itemId}`,
  })
}

const reorderChecklist = (axios: IFactoryFunctionRequest) => (trainingId: string, orderedIds: string[]): IFactoryResponse<any> => {
  return axios({
    method: EMethod.patch,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/${trainingId}/checklist/reorder`,
    data: { orderedIds },
  })
}

// Budget endpoints
const getBudget = (axios: IFactoryFunctionRequest) => (trainingId: string): IFactoryResponse<any> => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/${trainingId}/budget`,
  })
}

const addBudgetLine = (axios: IFactoryFunctionRequest) => (trainingId: string, data: any): IFactoryResponse<any> => {
  return axios({
    method: EMethod.post,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/${trainingId}/budget`,
    data,
  })
}

const updateBudgetLine = (axios: IFactoryFunctionRequest) => (trainingId: string, lineId: string, data: any): IFactoryResponse<any> => {
  return axios({
    method: EMethod.patch,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/${trainingId}/budget/${lineId}`,
    data,
  })
}

const deleteBudgetLine = (axios: IFactoryFunctionRequest) => (trainingId: string, lineId: string): IFactoryResponse<any> => {
  return axios({
    method: EMethod.delete,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/${trainingId}/budget/${lineId}`,
  })
}

const syncBudgetToFutureSessions = (axios: IFactoryFunctionRequest) => (trainingId: string): IFactoryResponse<{ updatedSessionsCount: number }> => {
  return axios({
    method: EMethod.post,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/${trainingId}/budget/syncToFutureSessions`,
  })
}

// Interested resources endpoints
const getInterested = (axios: IFactoryFunctionRequest) => (trainingId: string, type?: 'waitingList' | 'trainingPlan' | 'all'): IFactoryResponse<any> => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/${trainingId}/interested`,
    query: type ? { type } : {},
  })
}

const addInterested = (axios: IFactoryFunctionRequest) => (trainingId: string, resourceId: string, isTrainingPlan: boolean = false): IFactoryResponse<any> => {
  return axios({
    method: EMethod.post,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/${trainingId}/interested`,
    data: { resourceId, isTrainingPlan },
  })
}

const addInterestedBulk = (axios: IFactoryFunctionRequest) => (trainingId: string, resourceIds: string[], isTrainingPlan: boolean = false): IFactoryResponse<any> => {
  return axios({
    method: EMethod.post,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/${trainingId}/interested/bulk`,
    data: { resourceIds, isTrainingPlan },
  })
}

const removeInterested = (axios: IFactoryFunctionRequest) => (trainingId: string, entryId: string): IFactoryResponse<any> => {
  return axios({
    method: EMethod.delete,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/${trainingId}/interested/${entryId}`,
  })
}

const moveInterested = (axios: IFactoryFunctionRequest) => (trainingId: string, entryId: string, isTrainingPlan: boolean): IFactoryResponse<any> => {
  return axios({
    method: EMethod.patch,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/${trainingId}/interested/${entryId}/move`,
    data: { isTrainingPlan },
  })
}

// Document endpoints (v2)
const getDocuments = (axios: IFactoryFunctionRequest) => (trainingId: string): IFactoryResponse<any> => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/${trainingId}/documents`,
  })
}

const uploadDocument = (axios: IFactoryFunctionRequest) => (trainingId: string, data: { fileName: string, fileType: string, fileSize?: number, base64: string, fileDescription?: string, isPublic?: boolean }): IFactoryResponse<any> => {
  return axios({
    method: EMethod.post,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/${trainingId}/documents`,
    data,
  })
}

const updateDocumentMetadata = (axios: IFactoryFunctionRequest) => (trainingId: string, documentId: string, data: { fileDescription?: string, isPublic?: boolean, isLocked?: boolean }): IFactoryResponse<any> => {
  return axios({
    method: EMethod.patch,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/${trainingId}/documents/${documentId}`,
    data,
  })
}

const deleteDocument = (axios: IFactoryFunctionRequest) => (trainingId: string, documentId: string): IFactoryResponse<any> => {
  return axios({
    method: EMethod.delete,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/${trainingId}/documents/${documentId}`,
  })
}

// Update training basic info (v2)
const updateBasicInfo = (axios: IFactoryFunctionRequest) => (trainingId: string, data: any): IFactoryResponse<ITraining> => {
  return axios({
    method: EMethod.patch,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/${trainingId}`,
    data,
  })
}

const updateImage = (axios: IFactoryFunctionRequest) => (trainingId: string, data: { fileName: string, fileType: string, base64: string }): IFactoryResponse<any> => {
  return axios({
    method: EMethod.patch,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/${trainingId}/image`,
    data,
  })
}

const updateTraining = (axios: IFactoryFunctionRequest) => (trainingId: string, data: any) => {
  return axios({
    method: EMethod.put,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/${trainingId}`,
    data,
  })
}

export type WishesAttributionCell = {
  hasWish: boolean
  evaluationId?: string
  completedYear?: number
  absentYear?: number
  scheduledYear?: number
  status?: string | null
  rank?: number | null
  attributedAt?: string | null
  attributedSessionId?: string | null
}

export type WishesAttributionResource = {
  _id: string
  firstname: string
  lastname: string
  picture?: string
  office: string
  team: string
  coach: string
  job: string
  alumniDepartureDate?: string | Date | null
  firstJobStartDate?: string | Date | null
  isNewHire?: boolean
  wishRank?: number | null
  trainingCountInPeriod: number
  trainingDaysInPeriod: number
  wishCount: number
  wishDays: number
}

export type WishesAttributionTraining = {
  _id: string
  display: string
  minimum?: number
  maximum?: number
  duration?: number
  isHours?: boolean
}

/** A learning path wished in an evaluation, enrolled from the wishes attribution page */
export type WishesAttributionLearningPathWish = {
  evaluationId: string
  evaluationDisplay?: string | null
  resource: { _id: string, firstname: string, lastname: string, picture?: unknown }
  learningPathId: string
  learningPathDisplay: string
  stepsCount: number
  status: 'rejected' | null
  attributedAt?: string | null
  enrollment: { _id: string, status: 'in_progress' | 'completed' | 'closed', progress: { done: number, total: number, percent: number } } | null
}

export type WishesAttributionResponse = {
  evaluationsCount: number
  resources: WishesAttributionResource[]
  trainings: WishesAttributionTraining[]
  cells: Record<string, WishesAttributionCell>
  learningPathWishes?: WishesAttributionLearningPathWish[]
}

const getWishesAttribution = (axios: IFactoryFunctionRequest<WishesAttributionResponse, WishesAttributionResponse>) => (query: Record<string, unknown>) => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/wishes-attribution`,
    query,
  })
}

/** State of a cell in the exported workbook, coloured like the on-screen legend */
export type TWishesExcelTone =
  | 'completed' | 'absent' | 'scheduled' | 'assigned' | 'rejected'
  | 'wish' | 'priority' | 'validated' | 'newHire' | 'alumni' | 'muted'

export type TWishesExcelColumn = {
  label: string
  type?: 'text' | 'wrap' | 'number' | 'days' | 'percent'
  bold?: boolean
  /** Sums the column under the table, ignoring the rows hidden by the Excel filter */
  total?: boolean
  width?: number
}

export type TWishesExcelRow = {
  values: Array<string | number | null>
  tones?: Array<TWishesExcelTone | null>
  /** Matrix only: one entry per training column */
  cells?: Array<{ text: string, tone: TWishesExcelTone } | null>
}

export type TWishesExcelTable = {
  name: string
  subtitle?: string
  columns: TWishesExcelColumn[]
  rows: TWishesExcelRow[]
  totalLabel?: string
  autoFilter?: boolean
}

export type TWishesAttributionExcelRequest = {
  fileName: string
  title: string
  subtitle: string
  legend: { title: string, entries: { tone: TWishesExcelTone, label: string }[] }
  matrix: Omit<TWishesExcelTable, 'subtitle' | 'autoFilter'> & {
    groups: { label: string, span: number }[]
    trainings: { label: string, detail?: string }[]
  }
  tables: TWishesExcelTable[]
}

const exportWishesAttribution = (axios: IFactoryFunctionRequest<{ base64: string, fileName: string }, TWishesAttributionExcelRequest>) => (
  payload: TWishesAttributionExcelRequest,
) => {
  return axios({
    method: EMethod.post,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/wishes-attribution/export`,
    data: payload,
  })
}

const updateWishesStatus = (axios: IFactoryFunctionRequest<{ updated: number }, { updated: number }>) => (
  wishes: { evaluationId: string, trainingId: string }[],
  action: 'reject' | 'restore',
) => {
  return axios({
    method: EMethod.patch,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/wishes-attribution/wishes`,
    data: { wishes, action },
  })
}

const enrollLearningPathWishes = (axios: IFactoryFunctionRequest<{ enrolled: number }, { enrolled: number }>) => (
  wishes: { evaluationId: string, learningPathId: string }[],
) => {
  return axios({
    method: EMethod.post,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/wishes-attribution/learning-paths/enroll`,
    data: { wishes },
  })
}

const updateLearningPathWishesStatus = (axios: IFactoryFunctionRequest<{ updated: number }, { updated: number }>) => (
  wishes: { evaluationId: string, learningPathId: string }[],
  action: 'reject' | 'restore',
) => {
  return axios({
    method: EMethod.patch,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/wishes-attribution/learning-paths/status`,
    data: { wishes, action },
  })
}

export default (axios: AxiosInstance) => ({
  filter: filter(useAxios(axios)),
  getAll: getAll(useAxios(axios)),
  getOne: getOne(useAxios(axios)),
  updateOne: updateOne(useAxios(axios)),
  addDocument: addDocument(useAxios(axios)),
  createOne: createOne(useAxios(axios)),
  catalog: catalog(useAxios(axios)),
  catalogTraingDetail: catalogTraingDetail(useAxios(axios)),
  getDetails: getDetails(useAxios(axios)),
  getWishes: getWishes(useAxios(axios)),
  // V2 CRUD endpoints
  toggleActive: toggleActive(useAxios(axios)),
  softDelete: softDelete(useAxios(axios)),
  duplicate: duplicate(useAxios(axios)),
  // Checklist
  getChecklist: getChecklist(useAxios(axios)),
  addChecklistItem: addChecklistItem(useAxios(axios)),
  updateChecklistItem: updateChecklistItem(useAxios(axios)),
  deleteChecklistItem: deleteChecklistItem(useAxios(axios)),
  reorderChecklist: reorderChecklist(useAxios(axios)),
  // Budget
  getBudget: getBudget(useAxios(axios)),
  addBudgetLine: addBudgetLine(useAxios(axios)),
  updateBudgetLine: updateBudgetLine(useAxios(axios)),
  deleteBudgetLine: deleteBudgetLine(useAxios(axios)),
  syncBudgetToFutureSessions: syncBudgetToFutureSessions(useAxios(axios)),
  // Interested
  getInterested: getInterested(useAxios(axios)),
  addInterested: addInterested(useAxios(axios)),
  addInterestedBulk: addInterestedBulk(useAxios(axios)),
  removeInterested: removeInterested(useAxios(axios)),
  moveInterested: moveInterested(useAxios(axios)),
  // Documents (v2)
  getDocuments: getDocuments(useAxios(axios)),
  uploadDocument: uploadDocument(useAxios(axios)),
  updateDocumentMetadata: updateDocumentMetadata(useAxios(axios)),
  deleteDocument: deleteDocument(useAxios(axios)),
  // Update
  updateBasicInfo: updateBasicInfo(useAxios(axios)),
  updateImage: updateImage(useAxios(axios)),
  updateTraining: updateTraining(useAxios(axios)),
  getWishesAttribution: getWishesAttribution(useAxios(axios)),
  exportWishesAttribution: exportWishesAttribution(useAxios(axios)),
  updateWishesStatus: updateWishesStatus(useAxios(axios)),
  enrollLearningPathWishes: enrollLearningPathWishes(useAxios(axios)),
  updateLearningPathWishesStatus: updateLearningPathWishesStatus(useAxios(axios)),
})
